import { usePathname, useRouter } from "next/navigation";
import { useQueryState } from "nuqs";
import type { User } from "oidc-client-ts";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AuthContext,
  AuthProviderProps,
  AuthProvider as OidcAuthProvider,
  useAuth,
} from "react-oidc-context";

import { PublicRoute, Route } from "@/enums/route";
import { isTestEnv } from "@/lib/env";
import { noteSilentSignal } from "@/lib/noteSilentSignal";
import { reportSilentFailure } from "@/lib/reportSilentFailure";
import { useMockMode } from "@/providers/MockModeProvider";

import AuthController from "./AuthController";
import { createMockAuthContextValue, createMockUser } from "./mockAuthContext";
import { resolveAuthFailureReport } from "./resolveAuthFailureReport";
import {
  ACCESS_TOKEN_EXPIRING_NOTIFICATION_SECONDS,
  clearRenewalSkippedWhileHidden,
  getLastRenewalErrorMessage,
  getOidcUserManager,
  OIDC_SCOPE_INCLUDES_OFFLINE_ACCESS,
  wasRenewalSkippedWhileHidden,
} from "./userManagerSingleton";

// Delay before the single bounded mid-window renewal re-attempt after a
// silent-renew failure. Long enough for a transient blip (Keycloak deploy,
// network hiccup) to clear, short enough to land well inside the 60s
// pre-expiry window so the last-chance attempt at expiry still has room.
const MID_WINDOW_RETRY_DELAY_MS = 10_000;

const useOidcConfig = () => {
  // The UserManager lives in a module singleton (userManagerSingleton.ts) so
  // an AuthProvider remount during Next.js Suspense transitions can't create
  // duplicate SilentRenewService timers, and so non-React code (baseQuery's
  // 401 renew-and-retry) can share the same coalesced renewal.
  const [userManager] = useState(() => getOidcUserManager());

  const [oidcConfig] = useState<AuthProviderProps>({
    userManager: userManager as unknown as AuthProviderProps["userManager"],
    onSigninCallback: () => {
      // Clear pending items badge dismissal on login
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem("pending-items-badge-dismissed");
      }
      window.history.replaceState({}, document.title, window.location.pathname);
    },
  });

  return oidcConfig;
};

const useAuthRedirect = () => {
  const pathname = usePathname();
  const router = useRouter();

  const [autoLogin] = useQueryState("autoLogin");

  const isPublicRoute =
    pathname.includes(PublicRoute.Register) ||
    pathname.includes(PublicRoute.LoggedOut);

  const handleAuthenticatedPublicRoute = useCallback(() => {
    if (isPublicRoute) {
      router.push(Route.Dashboard);
    }
  }, [isPublicRoute, router]);

  return {
    isAutoLogin: autoLogin === "true",
    isPublicRoute,
    handleAuthenticatedPublicRoute,
  };
};

const AuthProtection = ({ children }: WithChildren) => {
  const auth = useAuth();

  const { isAutoLogin, isPublicRoute, handleAuthenticatedPublicRoute } =
    useAuthRedirect();

  // Latest auth held in a ref so the visibility listener (registered once) can
  // read current token state without re-subscribing on every auth change.
  const authRef = useRef(auth);
  useEffect(() => {
    authRef.current = auth;
  }, [auth]);

  // Latest public-route flag held in a ref so the once-registered auth-event
  // listeners below can read the CURRENT route without re-subscribing. A token
  // expiry / renewal failure on a public route (/register, /logged-out) is the
  // expected state for an unauthenticated visitor — not a failure to alert on
  // (CUSTOMER-UI-57 was 59 events / 33 users, almost all on /register).
  const isPublicRouteRef = useRef(isPublicRoute);
  useEffect(() => {
    isPublicRouteRef.current = isPublicRoute;
  }, [isPublicRoute]);

  // Warn at most once per page load if the realm doesn't grant the requested
  // offline_access scope (renewal robustness silently degrades without it).
  const offlineScopeWarnedRef = useRef(false);

  useEffect(() => {
    const docVisibility = () =>
      typeof document !== "undefined" ? document.visibilityState : "unknown";

    // One BOUNDED mid-window re-attempt after a failed foreground renewal.
    // oidc-client-ts gives up after a single silentRenewError (its own retry
    // only covers timeouts), so without this an outage lasting longer than
    // renewWithRetry's ~6s backoff would only get another chance at token
    // expiry. Bounded to once per renewal window (re-armed by USER_LOADED)
    // so a hard outage can't turn into a renewal request storm.
    let midWindowRetryTimer: ReturnType<typeof setTimeout> | undefined;
    let midWindowRetrySpent = false;
    const scheduleMidWindowRetry = () => {
      if (midWindowRetrySpent || midWindowRetryTimer) return;
      midWindowRetryTimer = setTimeout(() => {
        midWindowRetryTimer = undefined;
        midWindowRetrySpent = true;
        const user = authRef.current.user;
        // Already expired → the last-chance attempt in
        // handleAccessTokenExpired owns recovery; hidden → renewal is
        // deliberately deferred to the refocus handler.
        if (!user || user.expired || docVisibility() === "hidden") return;
        void authRef.current.signinSilent().catch(() => {
          // Failure surfaces via the ERROR state / the expiry path.
        });
      }, MID_WINDOW_RETRY_DELAY_MS);
    };

    const handleUserLoaded = (user: User) => {
      // A successful (re)load opens a fresh renewal window — re-arm the
      // bounded mid-window retry for it.
      midWindowRetrySpent = false;
      // offline_access opt-in observability — only runs when
      // NEXT_PUBLIC_AUTH_OFFLINE_SCOPE_ENABLED is on (off by default, since our
      // Keycloak clients don't grant the scope; see userManagerSingleton.ts).
      // If someone enables it against a realm that grants only a subset,
      // renewal stays capped by SSO session idle — surface that misconfig
      // instead of rediscovering it through CUSTOMER-UI-57 volume. (Guard on a
      // non-empty scope list: some responses omit `scope` entirely, which
      // proves nothing either way.)
      if (
        OIDC_SCOPE_INCLUDES_OFFLINE_ACCESS &&
        !offlineScopeWarnedRef.current &&
        user.scopes.length > 0 &&
        !user.scopes.includes("offline_access")
      ) {
        offlineScopeWarnedRef.current = true;
        reportSilentFailure({
          message:
            "[Auth] offline_access requested but not granted — refresh token lifetime stays capped by SSO session idle",
          level: "warning",
          fingerprint: "auth-offline-scope-not-granted",
          extra: { grantedScopes: user.scopes.join(" ") },
        });
      }
    };

    // Both handlers below emit an observable-only signal (noteSilentSignal),
    // NOT a Sentry issue. They were promoted to Sentry events in 2026-06 to
    // measure how often renewal degrades — that measurement has since been
    // answered by `resolveAuthFailureReport`: without `offline_access` (which
    // our Keycloak clients cannot grant) refresh tokens die at the SSO Session
    // Idle cap, so a share of foreground renewal failures is EXPECTED churn no
    // app-side change can remove. Alerting on an outcome we can't act on is
    // noise, so the trend now lives in Kibana + the Mixpanel replay console
    // instead of the issue stream. The genuinely actionable auth failures still
    // report: `auth-redirect-budget-exhausted` and `auth-signout-failed`.
    const handleSilentRenewError = (error: Error) => {
      // A SilentRenewError only fires when a REAL renewal request was attempted
      // (the signinSilent wrapper returns null without firing when it skips
      // while hidden) AND renewWithRetry's transient retries were exhausted —
      // so this is a genuine, sustained foreground-initiated failure. Do NOT
      // gate on arrival-time `docHidden`: the user may have backgrounded the tab
      // after initiating, and that should not mute a real degradation signal.
      // Two states are skipped entirely:
      //  - public route — no session intent (CUSTOMER-UI-56 noise on /register);
      //  - a genuine session-end (invalid_grant / "Token is not active") — an
      //    expected re-auth, not a bug, even on a protected route (replay 487874ce).
      // The decision is load-bearing beyond telemetry: it also gates the
      // bounded mid-window re-attempt below, which must not fire for a session
      // that is definitively over.
      const decision = resolveAuthFailureReport({
        isPublicRoute: isPublicRouteRef.current,
        renewalSkippedWhileHidden: false,
        docHidden: false,
        errorMessage: error.message,
      });
      if (decision !== "report") {
        console.warn(
          `[Auth] Silent token renewal failed — skipped (${decision})`,
          error.message
        );
        return;
      }
      noteSilentSignal({
        message: "[Auth] Silent token renewal failed",
        signal: "auth-silent-renew-failed",
        tags: { "doc.visibility": docVisibility() },
        extra: { errorMessage: error.message },
      });
      // A reportable failure is by definition NOT a dead session — give the
      // renewal another chance before the token runs out, so most transient
      // outages recover without the user ever noticing.
      scheduleMidWindowRetry();
    };
    const handleAccessTokenExpiring = () => {
      // No-op: silent renewal is driven by automaticSilentRenew.
    };
    const handleAccessTokenExpired = () => {
      // Decide whether this expiry is worth an alert. Skipped when:
      //  - on a public route — an unauthenticated visitor's stored token
      //    expiring is expected, not a failure (CUSTOMER-UI-57); or
      //  - renewal was deliberately skipped while hidden (or the tab is still
      //    hidden) — the ACCEPTED outcome of the background-renewal policy.
      // Both leave a breadcrumb only, visible in the Mixpanel replay console +
      // Kibana so an eventual "bounced to login on return" stays explainable.
      const decision = resolveAuthFailureReport({
        isPublicRoute: isPublicRouteRef.current,
        renewalSkippedWhileHidden: wasRenewalSkippedWhileHidden(),
        docHidden: docVisibility() === "hidden",
      });
      if (decision !== "report") {
        clearRenewalSkippedWhileHidden();
        console.warn(
          decision === "skip-public"
            ? "[Auth] Access token expired on a public route — expected, not alerting"
            : "[Auth] Access token expired in background — renewal intentionally skipped while hidden"
        );
        return;
      }
      const noteExpiryWithoutRenewal = (lastChanceOutcome: string) => {
        // react-oidc-context's signinSilent SWALLOWS the renewal rejection
        // (dispatches ERROR, resolves null), so the true Keycloak error is
        // recovered from the singleton, which records every final renewal
        // failure. Session age distinguishes SSO-Session-Max deaths (fixed
        // age) from idle/rotation causes when reading the log back.
        const user = authRef.current.user;
        const authTime = user?.profile?.auth_time;
        noteSilentSignal({
          message:
            "[Auth] Access token expired without successful silent renewal",
          signal: "auth-token-expired-unrenewed",
          tags: { "doc.visibility": docVisibility() },
          extra: {
            lastChanceOutcome,
            lastRenewalError: getLastRenewalErrorMessage() ?? "none-recorded",
            sessionAgeSeconds:
              typeof authTime === "number"
                ? Math.round(Date.now() / 1000 - authTime)
                : "unknown",
            tokenExpiresAt: user?.expires_at ?? "unknown",
          },
        });
      };
      // Expired while visible on a protected route with no skip to explain
      // it. Before recording anything (and before AuthController's redirect
      // can fire), make a LAST-CHANCE renewal attempt: the refresh token
      // routinely outlives the access token, so this recovers the session in
      // place — no full-page Keycloak bounce, no lost in-progress work. Only
      // an expiry that survives this attempt is worth recording at all.
      void authRef.current
        .signinSilent()
        .then((user) => {
          if (user) {
            console.warn(
              "[Auth] Access token expired but last-chance silent renewal recovered the session"
            );
            return;
          }
          noteExpiryWithoutRenewal("renewal-skipped-or-null");
        })
        .catch((error: unknown) => {
          noteExpiryWithoutRenewal(
            error instanceof Error ? error.message : "renewal-rejected"
          );
        });
    };

    auth.events.addUserLoaded(handleUserLoaded);
    auth.events.addSilentRenewError(handleSilentRenewError);
    auth.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    auth.events.addAccessTokenExpired(handleAccessTokenExpired);

    return () => {
      if (midWindowRetryTimer) clearTimeout(midWindowRetryTimer);
      auth.events.removeUserLoaded(handleUserLoaded);
      auth.events.removeSilentRenewError(handleSilentRenewError);
      auth.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      auth.events.removeAccessTokenExpired(handleAccessTokenExpired);
    };
  }, [auth.events]);

  // Re-attempt the silent renewal we deferred while the tab was hidden, as
  // soon as the session becomes usable again: the tab returns to the
  // foreground (visibilitychange), connectivity is restored (online — a
  // renewal that failed offline can now succeed), or the page is restored
  // from the back/forward cache (pageshow — its timers were frozen, so the
  // token may have lapsed without any event firing). If the session is still
  // alive the user stays logged in seamlessly (no full-page redirect / splash
  // flash — the boot-stuck path, CUSTOMER-UI-3Z); if it's dead, the failed
  // renewal triggers the normal sign-in redirect.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const renewIfStale = () => {
      if (document.visibilityState !== "visible") return;
      const user = authRef.current.user;
      if (!user) return;
      const expiresInMs = (user.expires_at ?? 0) * 1000 - Date.now();
      // Only when actually needed: already expired, or inside the same
      // pre-expiry window the automatic renew uses. (A fresh token no-ops,
      // so the extra pageshow firing on normal loads costs nothing.)
      if (
        user.expired ||
        expiresInMs < ACCESS_TOKEN_EXPIRING_NOTIFICATION_SECONDS * 1000
      ) {
        void authRef.current.signinSilent().catch(() => {
          // Failure surfaces via the silentRenewError listener / redirect path.
        });
      }
    };
    document.addEventListener("visibilitychange", renewIfStale);
    window.addEventListener("online", renewIfStale);
    window.addEventListener("pageshow", renewIfStale);
    return () => {
      document.removeEventListener("visibilitychange", renewIfStale);
      window.removeEventListener("online", renewIfStale);
      window.removeEventListener("pageshow", renewIfStale);
    };
  }, []);

  return (
    <AuthController
      {...auth}
      isAutoLogin={isAutoLogin}
      isPublicRoute={isPublicRoute}
      onAuthenticatedPublicRoute={handleAuthenticatedPublicRoute}
    >
      {children}
    </AuthController>
  );
};

const AuthProvider = ({ children }: WithChildren) => {
  const oidcConfig = useOidcConfig();
  return (
    <OidcAuthProvider {...oidcConfig}>
      <AuthProtection>{children}</AuthProtection>
    </OidcAuthProvider>
  );
};

type MockAuthProtectionProps = WithChildren<{ user: User }>;

const MockAuthProtection = ({ children, user }: MockAuthProtectionProps) => {
  const { isAutoLogin, isPublicRoute, handleAuthenticatedPublicRoute } =
    useAuthRedirect();

  return (
    <AuthController
      isAutoLogin={isAutoLogin}
      isPublicRoute={isPublicRoute}
      onAuthenticatedPublicRoute={handleAuthenticatedPublicRoute}
      isLoading={false}
      isAuthenticated={true}
      user={user}
      signinRedirect={async () => {}}
      bypassGuards
    >
      {children}
    </AuthController>
  );
};

// Build the fake user once per mount (stable identity — render-stability).
const useMockUser = () =>
  useState(() => createMockUser({ nowMs: Date.now(), sub: "mock-user" }))[0];

/**
 * E2E test build. Keeps the real OidcAuthProvider on purpose: the E2E specs
 * for logout (inactivity, account-state-gate) assert the real UserManager's
 * RP-initiated sign-out navigates to Keycloak's end-session URL, which
 * Playwright intercepts. Only the route gate is mocked here.
 */
const TestAuthProvider = ({ children }: WithChildren) => {
  const oidcConfig = useOidcConfig();
  const user = useMockUser();
  return (
    <OidcAuthProvider {...oidcConfig}>
      <MockAuthProtection user={user}>{children}</MockAuthProtection>
    </OidcAuthProvider>
  );
};

/**
 * Runtime mock mode. Provides AuthContext directly instead of mounting
 * OidcAuthProvider, so this tree never creates a UserManager: a stored session
 * can't arm silent-renew timers (oidc-client-ts's SilentRenewService calls
 * signinSilent against Keycloak once a loaded token nears expiry), a stray
 * ?code&state can't run a signin callback, and `useAuth()` / `useJwt()` see
 * the fake user's roles. Other paths to Keycloak/BFF are closed elsewhere
 * (MockModeGate, baseQuery, mocks/handlers.ts), not here.
 */
const MockModeAuthProvider = ({ children }: WithChildren) => {
  const user = useMockUser();
  const [value] = useState(() => createMockAuthContextValue(user));
  return (
    <AuthContext.Provider value={value}>
      <MockAuthProtection user={user}>{children}</MockAuthProtection>
    </AuthContext.Provider>
  );
};

// Build-time test env (E2E, Storybook) is a compile-time constant; runtime
// mock mode comes from MockModeGate, which renders nothing until it has
// decided — so this read is never a first-render guess.
const RuntimeAuthProvider = ({ children }: WithChildren) => {
  const { isMockMode } = useMockMode();
  if (isTestEnv) return <TestAuthProvider>{children}</TestAuthProvider>;
  if (isMockMode)
    return <MockModeAuthProvider>{children}</MockModeAuthProvider>;
  return <AuthProvider>{children}</AuthProvider>;
};

export default RuntimeAuthProvider;
